export const PROFILE_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const PROFILE_PHOTO_MAX_DIMENSION = 1024;
export const PROFILE_PHOTO_OUTPUT_MIME_TYPE = "image/jpeg";

const PROFILE_PHOTO_OUTPUT_QUALITY = 0.85;
const ALLOWED_PROFILE_PHOTO_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export const resizeProfilePhotoFile = async (file: File): Promise<File> => {
  if (!ALLOWED_PROFILE_PHOTO_MIME_TYPES.has(file.type)) {
    throw new Error("Profile photos must be JPEG, PNG, or WebP images.");
  }

  const image = await loadImage(file);
  const dimensions = resolveScaledDimensions({
    width: image.naturalWidth,
    height: image.naturalHeight,
    maxWidth: PROFILE_PHOTO_MAX_DIMENSION,
    maxHeight: PROFILE_PHOTO_MAX_DIMENSION,
  });

  const canvas = document.createElement("canvas");
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not prepare image canvas.");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, dimensions.width, dimensions.height);
  context.drawImage(image, 0, 0, dimensions.width, dimensions.height);

  const blob = await canvasToBlob({ canvas });

  if (blob.size > PROFILE_PHOTO_MAX_BYTES) {
    throw new Error("Profile photo must be 8 MB or smaller after resizing.");
  }

  return new File([blob], buildOutputFilename(file.name), {
    type: PROFILE_PHOTO_OUTPUT_MIME_TYPE,
    lastModified: Date.now(),
  });
};

const loadImage = async (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not load image."));
    };

    image.src = objectUrl;
  });

const resolveScaledDimensions = ({
  width,
  height,
  maxWidth,
  maxHeight,
}: {
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
}) => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error("Image dimensions are invalid.");
  }

  const scale = Math.min(1, maxWidth / width, maxHeight / height);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const canvasToBlob = async ({ canvas }: { canvas: HTMLCanvasElement }): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error("Could not encode image."));
          return;
        }

        resolve(blob);
      },
      PROFILE_PHOTO_OUTPUT_MIME_TYPE,
      PROFILE_PHOTO_OUTPUT_QUALITY
    );
  });

const buildOutputFilename = (filename: string) => {
  const trimmedFilename = filename.trim();
  const baseFilename = trimmedFilename ? trimmedFilename.replace(/\.[^.]*$/, "") : "profile-photo";
  return `${baseFilename || "profile-photo"}.jpg`;
};
